/**
 * List of different filing states
 */

import type {
  CardTypes,
  SensitiveInfo,
  StatusPredicateKeys,
  TaxClientInfo,
} from "./types";

import type { Icon } from "@tabler/icons-react";
import {
  IconBabyCarriage,
  IconBalloon,
  IconBriefcase,
  IconBrowser,
  IconBrowserCheck,
  IconCar,
  IconCash,
  IconFileUpload,
  IconInfoCircle,
  IconLeaf,
  IconLoader,
  IconMail,
  IconMapPin,
  IconPhone,
  IconReceipt2,
  IconSchool,
  IconUser,
  IconUserCheck,
  IconUserPlus,
  IconUsersPlus,
  IconWriting,
} from "@tabler/icons-react";

export const backend_url = "https://api.smartwiz.link";
export const error_reports = "false"; //create error report tickets
export const stripe_url = "https://buy.stripe.com/test_8wMaEHfVt1Jf5eEcMQ";
export const onboarding_url = "https://hello.smartwiz.io/meetings/demo-bre/official-onboarding-call";
export const support_url = "https://hub.smartwiz.ai";
export const support_team_capacity = "https://docs.google.com/forms/d/1sLtlygWvKW1jTEOGtsIj8hU8ZoQfI9p9Q220q6Sh5u4/viewform?edit_requested=true";

// File Upload Configuration
//
// DELIBERATELY LOWER than the company-wide ceiling. Do NOT "fix" this to match
// `MAX_DOCUMENT_UPLOAD_SIZE` (100 MB, `backend/src/common/storage/document_upload_service.py`).
//
// Why: this legacy app has NO chunked upload. The extension and intake both split anything
// over 1 MB via their `CHUNK_SIZE_BYTES` paths; here the file goes as a single whole-file
// request, which becomes a ~133 MB body after base64 at the 100 MB ceiling and is slow or
// times out on a poor connection.
//
// So 40 MB is the limit this app can actually honor, and the honest number to show a pro.
// Advertising 100 MB here would accept an upload we then fail to deliver — worse than
// rejecting it up front (product decision, 7/28/26). Larger documents belong in intake or
// the extension, which chunk properly and do support 100 MB.
//
// If chunked upload is ever added here, raise this to match the backend ceiling.
export const MAX_FILE_SIZE_MB = 40;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const enum ClientStatus {
  New = "New",
  WAITING = "Waiting",
  IN_PROGRESS = "In Progress",
  QUOTE_SENT = "Quote Sent",
  SIGNATURES_NEEDED = "Signatures Needed",
  READY_FILE = "Ready To E-File",
  COMPLETED = "Completed",
  ARCHIVED = "Archived",
  READY_TO_GO = "Ready To Go",
}

/**
 * a list of options for inactive
 */
export const inactiveOptions = {
  "3 days": 3,
  "1 week": 7,
  "2 weeks": 14,
  "1 month": 30,
};
// Extract the string keys from the Object
export const optionStrings = Object.keys(inactiveOptions);

/**
 * List of different client states and CSS class
 */
export const clientStatuses = {
  New: {
    class: "F9C599",
    status: ClientStatus.New,
  },
  "In Progress": {
    class: "6CBAF2",
    status: ClientStatus.IN_PROGRESS,
  },

  Waiting: {
    class: "F3E1A4",
    status: ClientStatus.WAITING,
  },
  "Quote Sent": {
    class: "D6F0FF",
    status: ClientStatus.QUOTE_SENT,
  },
  "Signatures Needed": {
    class: "FDD3D0",
    status: ClientStatus.SIGNATURES_NEEDED,
  },
  "Ready To E-File": {
    class: "CECDF8",
    status: ClientStatus.READY_FILE,
  },
  Completed: {
    class: "D7F7DC",
    status: ClientStatus.COMPLETED,
  },
  Archived: {
    class: "D9D9D9",
    status: ClientStatus.ARCHIVED,
  },
  "Ready To Go": {
    class: "5f7f64",
    status: ClientStatus.READY_TO_GO,
  },
};

// can save applied filters in local storage and then apply them again on refresh if want to persist filters

// imported from constants
export const statusPredicates: Record<
  StatusPredicateKeys,
  (client: any) => boolean
> = {
  new: (client: any) => client["status"] === ClientStatus.New,
  "in progress": (client: any) => client["status"] === ClientStatus.IN_PROGRESS,
  waiting: (client: any) => client["status"] === ClientStatus.WAITING,
  "quote sent": (client: any) => client["status"] === ClientStatus.QUOTE_SENT,
  "signatures needed": (client: any) =>
    client["status"] === ClientStatus.SIGNATURES_NEEDED,
  "ready to e-file": (client: any) =>
    client["status"] === ClientStatus.READY_FILE,
  completed: (client: any) => client["status"] === ClientStatus.COMPLETED,
  "ready to go": (client: any) => client["status"] === ClientStatus.READY_TO_GO,
};

export const getGreetingBasedOnTime = () => {
  const hour = new Date().getHours();
  if (hour >= 3 && hour < 11) {
    return "Good Morning";
  } else if (hour >= 11 && hour < 19) {
    return "Good Afternoon";
  } else {
    return "Good Evening";
  }
};

export const emailSelect = [
  "Refund Status",
  "Copy Return",
  "Return Efiled",
  "Return Accepted",
  "Follow Documents",
  "Follow Signatures",
  "Confirmation Prepared",
  "Welcome Return",
  "Google Review",
  "Reminder Deadlines",
];

export const htmlTemplate = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Basic HTML Template</title>
</head>
<body>
    <h1>Hello World!</h1>
</body>
</html>
`;

/* Tax Organizer */
/**
 * @description generates the General Information dropdown from the user object
 * @params user - gets broken down to return dropdown
 * @returns General Information dropdown
 */
export const generalData: TaxClientInfo[] = [
  {
    title: "Address",
    data: "1234 Main St, City, State, 12345",
    dataType: "address",
    icon: IconMapPin,
  },
  {
    title: "Email",
    data: "james@gmail.com",
    dataType: "string",
    icon: IconMail,
  },
  {
    title: "Phone",
    data: "123-456-7890",
    dataType: "phone",
    icon: IconPhone,
  },
  {
    title: "Birthdate",
    data: "01/01/1990",
    dataType: "date",
    icon: IconBalloon,
  },
  {
    title: "Occupation",
    data: "Software Engineer",
    dataType: "string",
    icon: IconBriefcase,
  },
];

/**
 * type for TaxPayer/client's data in the client's details
 * title: name of the section the data is for e.g. (address, bank name, etc.)
 * data: actual data that goes under the title
 * dataType: type of data
 * hidden?: whether the particular data should be obfuscated
 * icon: the tabler react icon
 * prefix: text that goes before the data
 * document: document info
 */
export type TaxPayerInfo = {
  title: string;
  data: string | number | object;
  dataType: string;
  hidden?: boolean;
  icon: Icon;
  prefix?: string;
  document?: Document | undefined;
};

export const testData = [
  {
    title: "Name",
    data: "Big Joe",
    dataType: "string",
    icon: IconUser,
  },
  {
    title: "Email",
    data: "joe@gmail.com",
    dataType: "string",
    icon: IconMail,
  },
  {
    title: "Phone",
    data: "123-456-7890",
    dataType: "phone",
    icon: IconPhone,
  },
  {
    title: "Birthdate",
    data: "01/01/1990",
    dataType: "date",
    icon: IconBalloon,
  },
  {
    title: "Occupation",
    data: "Software Engineer",
    dataType: "string",
    icon: IconBriefcase,
  },
];

export const spouseData = [
  {
    title: "Name",
    data: "Big Jane",
    icon: IconUser,
  },
  {
    title: "Email",
    data: "jane@gmail.com",
    icon: IconMail,
  },
  {
    title: "Phone",
    data: "123-456-7550",
    icon: IconPhone,
  },
  {
    title: "Birthdate",
    data: "01/02/1990",
    icon: IconBalloon,
  },
  {
    title: "Occupation",
    data: "Software Engineer",
    icon: IconBriefcase,
  },
];
type Field = {
  key: string;
  title: string;
  icon: string;
};

// helper function for mapping common fields
const createAddressMapping = (
  prefix: string,
  icon: any,
  category?: string
) => ({
  [`${prefix}.city`]: { title: `City`, icon, category },
  [`${prefix}.state`]: { title: `State`, icon, category },
  [`${prefix}.road`]: { title: `Road`, icon, category },
  [`${prefix}.postalCode`]: { title: `Zip`, icon, category },
  [`${prefix}.streetAddress`]: { title: `Street Address`, icon, category },
  [`${prefix}.houseNumber`]: { title: `House Number`, icon, category },
  [`${prefix}.unit`]: { title: `Unit`, icon, category },
  [`${prefix}.poBox`]: { title: `PO Box`, icon, category },
  [`${prefix}.Telephone`]: { title: `Telephone`, icon, category },
});

const createNameTINMapping = (
  prefix: string,
  icon: any,
  category?: string
) => ({
  [`${prefix}.Name`]: { title: "Name", icon, category },
  [`${prefix}.TIN`]: { title: "TIN", icon, category },
  [`${prefix}.AccountNumber`]: { title: "Account Number", icon, category },
});

function generateTaxesWithheldMapping(
  basePath: string,
  fields: Field[],
  count: number = 5
): { [key: string]: any } {
  const mapping: { [key: string]: any } = {};
  for (let i = 0; i < count; i++) {
    fields.forEach((field) => {
      mapping[`${basePath}.${i}.${field.key}`] = {
        title: `${field.title} ${i + 1}`,
        icon: field.icon,
      };
    });
  }
  return mapping;
}

const localTaxesFields: Field[] = [
  { key: "Box17", title: "Local Tax Withheld", icon: "17" },
  { key: "Box18", title: "Name of Locality", icon: "18" },
  { key: "Box19", title: "Local Distribution", icon: "19" },
];

const stateTaxesFields: Field[] = [
  { key: "Box10a", title: "State", icon: "10a" },
  { key: "Box10b", title: "State Identification Number", icon: "10b" },
  { key: "Box11", title: "State Tax Income Withheld", icon: "11" },
];

const stateTaxes1099necFields: Field[] = [
  { key: "Box5", title: "State Tax Withheld", icon: "5" },
  { key: "Box6", title: "State/Payer's State Number", icon: "6" },
  { key: "Box7", title: "State Income", icon: "7" },
];

function generateCommonMapping(formType: string): { [key: string]: any } {
  const commonMapping = {
    Box1: {
      title: "Gross Distribution",
      icon: "1",
    },
    Box2a: {
      title: "Taxable Amount",
      icon: "2a",
    },
    Box2b: {
      title: "Taxable Amount Not Determined",
      icon: "2b",
    },
    Box2bDetails: {
      title: "Details for Box 2b",
      icon: "2b",
    },
    Box3: {
      title: "Capital Gain",
      icon: "3",
    },
    Box4: {
      title: "Federal Income Tax Withheld",
      icon: "4",
    },
    Box5: {
      title: "Employee Contributions",
      icon: "5",
    },
    Box6: {
      title: "Net Realized Appreciation",
      icon: "6",
    },
    Box7: {
      title: "Distribution Codes",
      icon: "7",
    },
    Box8: {
      title: "Other",
      icon: "8",
    },
    Box8Percentage: {
      title: "Percentage for Box 8",
      icon: "8",
    },
    Box9a: {
      title: "Percentage of Total Distribution",
      icon: "9a",
    },
    Box9b: {
      title: "Total Employee Contributions",
      icon: "9b",
    },
    Box10: {
      title: "Amount Alocable to IRR",
      icon: "10",
    },
    Box11: {
      title: "1st Year of Roth Contribution",
      icon: "11",
    },
    Box12: {
      title: "FATCA Filing Requirement",
      icon: "12",
    },
    Box13: {
      title: "Date of PaymentStep",
      icon: "13",
    },
    Box14: {
      title: "State Tax Withheld",
      icon: 14,
    },
    Box15: {
      title: "State No.",
      icon: 15,
    },
    Box16: {
      title: "State Distribution",
      icon: 16,
    },
    Box17: {
      title: "Local Tax Withheld",
      icon: 17,
    },
    Box18: {
      title: "Name of Locaily",
      icon: 18,
    },
    Box19: {
      title: "Local Distribution",
      icon: 19,
    },
    ...createAddressMapping("Payer.Address", null, "Payer"),
    ...createNameTINMapping("Payer", null, "Payer"),

    ...createAddressMapping("Recipient.Address", null, "Recipient"),
    ...createNameTINMapping("Recipient", null, "Recipient"),
    ...generateTaxesWithheldMapping("LocalTaxesWithheld", localTaxesFields),
  };

  if (formType === "1099g") {
    return {
      ...commonMapping,
      Box1: {
        title: "Unemployment Compensation",
        icon: 1,
      },
      Box2: {
        title: "State or Local Income Tax Refunds, Credits",
        icon: 2,
      },
      Box3: {
        title: "Box 2 amount is for Tax Year",
        icon: 3,
      },
      Box4: {
        title: "Federal Income Tax Witheld",
        icon: 4,
      },
      Box5: {
        title: "RTAA Payments",
        icon: 5,
      },
      Box6: {
        title: "Taxable Grants",
        icon: 6,
      },
      Box7: {
        title: "Agriculture Payments",
        icon: 7,
      },
      Box8: {
        title: "Box 2 is Trade or Business Income",
        icon: 8,
      },
      Box9: {
        title: "Market Gain",
        icon: 9,
      },
      ...generateTaxesWithheldMapping("StateTaxesWithheld", stateTaxesFields),
    };
  }

  return commonMapping;
}

export const titleMapping: {
  [key: string]: {
    [key: string]: any | { title: any };
  };
} = {
  business: {
    name: "Business Name",
    address: "Business Address",
    addressline2: "Business Address Line 2",
    city: "Business City",
    state: "Business State",
    zip: "Business Zip",
    country: "Business Country",
    phone: "Business Phone",
    website: "Business Website",
    ein: "EIN",
    income: "Business Income",
    expenses: "Business Expenses",
  },
  dependents: {
    firstname: "First Name",
    lastname: "Last Name",
    dob: "Date of Birth",
    SSsn: "Social Security Number",
    tuitionname: "Tuition Name",
    tuitionpaid: "Tuition Paid",
    daycareprovider: "Day Care Provider",
    daycareproviderein: "Day Care Provider's Identification Number",
    daycarestatus: "Day Care Status",
    relationship: "Relationship",
  },
  spouseInfo: {
    email: "Email",
    firstname: "First Name",
    lastname: "Last Name",
    phonenumber: "Phone Number",
    occupation: "Occupation",
  },
  generalInfo: {
    clientaddress: "Address",
    clientaddressline2: "Address Line 2",
    clientcity: "City",
    clientcountry: "Country",
    clientstate: "State",
    clientzipcode: "Zip",
    clientoccupation: "Occupation",
    state2address: "Former Address",
    state2addressline2: "Former Address Line 2",
    state2city: "Former City",
    state2country: "Former Country",
    state2state: "Former State",
    state2zip: "Former Zip",
    directdepositbool: "Direct Deposit",
    filingstatus: "Filing Status",
    hasdep: "Has Dependents",
    numdep: "Number of Dependents",
    charitablecontributions: "Charitable Contributions",
  },
  // W2 form
  w2: {
    Employee: { title: "Employee", icon: null },
    Employer: { title: "Employer", icon: null },
    FederalIncomeTaxWithheld: {
      title: "Federal Income Tax Withheld",
      icon: 2,
    },
    IsRetirementPlan: { title: "Retirement Plan", icon: 13 },
    IsStatutoryEmployee: {
      title: "Statutory Employee",
      icon: 13,
    },
    IsThirdPartySickPay: { title: "Third Party Sick Pay", icon: 13 },
    MedicareWagesAndTips: {
      title: "Medicare Wages And Tips",
      icon: 5,
    },
    SocialSecurityTaxWithheld: {
      title: "Social Security Tax Withheld",
      icon: 4,
    },
    WagesTipsAndOtherCompensation: {
      title: "Wages, Tips And Other Compensation",
      icon: 1,
    },
    MedicareTaxWithheld: {
      title: "Medicare Tax Withheld",
      icon: 6,
    },
    Other: {
      title: "Other",
      icon: 14,
    },
    "AdditionalInfo.0.Amount": {
      title: "Additional Info Amount 1",
      icon: "12a",
    },
    "AdditionalInfo.1.Amount": {
      title: "Additional Info Amount 2",
      icon: "12b",
    },
    "AdditionalInfo.2.Amount": {
      title: "Additional Info Amount 3",
      icon: "12c",
    },
    "AdditionalInfo.3.Amount": {
      title: "Additional Info Amount 4",
      icon: "12d",
    },

    // Employee
    "Employee.SocialSecurityNumber": {
      title: "SSN",
      icon: "A",
    },
    ...createAddressMapping("Employee.Address", "F"),
    ...createNameTINMapping("Employee", "A"),
    // Employer
    ...createAddressMapping("Employer.Address", "C"),
    ...createNameTINMapping("Employer", "B"),
    "Employer.IdNumber": { title: "EIN", icon: "B" },

    // Misc
    "LocalTaxInfos.0.LocalityName": {
      title: "Locality Name and Type",
      icon: 20,
    },
    "LocalTaxInfos.0.LocalIncomeTax": {
      title: "Local Income Tax",
      icon: 19,
    },
    "LocalTaxInfos.0.LocalWagesTipsEtc": {
      title: "Local Wages Tips Etc",
      icon: 18,
    },
    "StateTaxInfos.0.StateIncomeTax": {
      title: "State Income Tax",
      icon: 17,
    },
    SocialSecurityWages: {
      title: "Social Security Wages",
      icon: 3,
    },
    "StateTaxInfos.0.EmployerStateId": {
      title: "Employer State Id",
      icon: 15,
    },
    "StateTaxInfos.0.State": { title: "State", icon: 15 },
    "StateTaxInfos.0.StateWagesTipsEtc": {
      title: "State Wages Tips Etc",
      icon: 16,
    },
    "StateTaxInfos.0.EmployerStateIdNumber": {
      title: "Employer State Id Number",
      icon: 15,
    },
    W2FormVariant: { title: "W2 Form Variant", icon: null },
  },

  //1098-E
  "1098e": {
    ...createAddressMapping("Borrower.Address", null, "Borrower"),
    ...createNameTINMapping("Borrower", null, "Borrower"),

    ExcludesFeesOrInterest: {
      title: "Excludes Fees Or Interest",
      icon: null,
    },

    IsCorrected: {
      title: "Is Corrected",
      icon: null,
    },

    ...createAddressMapping("Lender.Address", null, "Lender"),
    ...createNameTINMapping("Lender", null, "Lender"),

    StudentLoanInterest: {
      title: "Student Loan Interest",
      icon: 1,
    },

    TaxYear: {
      title: "Tax Year",
      icon: null,
    },
  },

  // 1098-T
  "1098t": {
    AdjustmentsForPriorYear: {
      title: "Adjustments For Prior Year",
      icon: 4,
    },
    ...createAddressMapping("Filer.Address", null, "Filer"),
    ...createNameTINMapping("Filer", null, "Filer"),

    TaxYear: {
      title: "Tax Year",
      icon: null,
    },
    IncludesAmountForNextPeriod: {
      title: "Includes Amount For Next Period",
      icon: 7,
    },
    IsCorrected: {
      title: "Changed Reporting Method",
      icon: 3,
    },
    InsuranceContractReimbursements: {
      title: "Insurance Contract Reimbursements",
      icon: 10,
    },
    IsAtLeastHalfTimeStudent: {
      title: "Is At Least Half Time Student",
      icon: 8,
    },
    IsGraduateStudent: {
      title: "Is Graduate Student",
      icon: 9,
    },
    PaymentReceived: {
      title: "PaymentStep Received",
      icon: 1,
    },
    Scholarships: {
      title: "Scholarships",
      icon: 5,
    },
    ScholarshipsAdjustments: {
      title: "Scholarship Adjustments",
      icon: 6,
    },
    ...createAddressMapping("Student.Address", null, "Student"),
    ...createNameTINMapping("Student", null, "Student"),
  },
  "1099nec": {
    Box1: {
      title: "Nonemployee Compensation",
      icon: 1,
    },
    ...createAddressMapping("Payer.Address", null, "Payer"),
    ...createNameTINMapping("Payer", null, "Payer"),

    Box2: {
      title: "Products For Resale",
      icon: 2,
    },
    ...createAddressMapping("Recipient.Address", null, "Recipient"),
    ...createNameTINMapping("Recipient", null, "Recipient"),
    ...generateTaxesWithheldMapping(
      "StateTaxesWithheld",
      stateTaxes1099necFields
    ),
  },

  "1099g": generateCommonMapping("1099g"), //1099-G
  "1099r": generateCommonMapping("1099r"), //1099-R

  id: {
    DateOfBirth: {
      title: "Date of Birth",
      icon: null,
    },
    DateOfExpiration: {
      title: "Date of Expiration",
      icon: null,
    },
    DateOfIssue: {
      title: "Date of Issue",
      icon: null,
    },
    DocumentNumber: {
      title: "ID Number",
      icon: null,
    },
    State: {
      title: "State",
      icon: null,
    },
  },
};

export const businessDataMapper = {
  firstname: "Name",
};

/**
 * a map of key to title and icon for secure info
 */
export const SecureInfoKeyToTitleMap: SensitiveInfo = {
  ssn: { title: "SSN" },
  bankAccountNumber: {
    title: "Bank Account #",
  },
  bankAccountType: {
    title: "Bank Account Type",
  },
  bankRoutingNumber: {
    title: "Bank Routing #",
  },
  ippin: {
    title: "IP PIN",
  },
};

export const eventTypes = {
  0: "newClient",
  1: "newDoc",
  2: "formFinished",
  3: "newInvoice",
  4: "newPayment",
  5: "newQuote",
  6: "other",
  7: "onboarding",
};
export const clientSteps = [
  { label: "Client Added", icon: IconUserPlus, description: "Client Added" },
  { label: "Onboarding", icon: IconUserCheck, description: "Onboarding" },
  {
    label: "Not Available",
    icon: IconInfoCircle,
    description: "Client Info",
  },
  {
    label: "Taxorganizer",
    icon: IconBrowserCheck,
    description: "Taxorganizer Completed",
  },
  {
    label: "Documents",
    icon: IconFileUpload,
    description: "Documents Uploaded",
  },
  { label: "Payments", icon: IconReceipt2, description: "Payments Recieved" },
  {
    label: "Signatures",
    icon: IconWriting,
    description: "Signatures Completed",
  },
];

export const cardsData: CardTypes[] = [
  {
    icon: IconBabyCarriage,
    title: "Child Tax Credit",
    info: "The Child Tax Credit is a tax credit provided to families with qualifying children under the age of 17.",
  },
  {
    icon: IconCar,
    title: "Clean Vehicle Credit",
    info: "The Clean Vehicle Credit is a tax credit provided to individuals who purchase qualifying electric or hybrid vehicles.",
  },
  {
    icon: IconUsersPlus,
    title: "Adoption Credit",
    info: "The Adoption Credit is a tax credit provided to adoptive parents to help offset the costs associated with adopting a child.",
  },
  {
    icon: IconSchool,
    title: "Education Credit",
    info: "The Education Credit is a tax credit provided to individuals who incur qualified education expenses, such as tuition and fees, for themselves, their spouse, or their dependents.",
  },
  {
    icon: IconLeaf,
    title: "Home Energy Credit",
    info: "The Home Energy Credit is a tax credit provided to homeowners who make qualifying energy-efficient improvements to their primary residences.",
  },
  {
    icon: IconCash,
    title: "Earned Income Tax Credit",
    info: "The Earned Income Tax Credit is a refundable tax credit provided to low-to-moderate-income individuals and families who meet certain eligibility criteria.",
  },
];

export const VAPIDKey =
  "BOL9TC0TZKmTDgE4voSV9pIuNlCOHFG5PjCotNpS3E3bY5knJDVUGy4cMC5sGtULCayhW05Il0ihTKZFjE8vwDM";

// questions for Jaz AI support default
export const questions = {
  taxorganizer: [
    {
      id: 1,
      question:
        "What details do I need to provide to get started with my tax filing?",
      enumeration: 0,
    },
    {
      id: 2,
      question: "How do I use the Tax Organizer feature?",
      enumeration: 1,
    },
  ],
  clienthome: [
    {
      id: 3,
      question: "What documents do I need to upload?",
      enumeration: 0,
    },
    {
      id: 4,
      question: "How can I upload my documents?",
      enumeration: 1,
    },
  ],
  documents: [
    {
      id: 5,
      question: "How can I upload a new document?",
      enumeration: 0,
    },
    {
      id: 6,
      question:
        "How do I view the details or contents of the uploaded document?",
      enumeration: 1,
    },
    {
      id: 7,
      question: "What should I do if my document isn't uploading properly?",
      enumeration: 2,
    },
  ],
  vault: [
    {
      id: 8,
      question: "What should I do if I don't have an IP PIN?",
      enumeration: 0,
    },
    {
      id: 9,
      question:
        "How is my sensitive information protected in the Identity Vault?",
      enumeration: 1,
    },
  ],
  settings: [
    {
      id: 10,
      question: "How do I change my email address?",
      enumeration: 0,
    },
    {
      id: 11,
      question: "What are the requirements for creating a new password?",
      enumeration: 1,
    },
    {
      id: 12,
      question: "Why can't I save my changes?",
      enumeration: 2,
    },
  ],
};

export const BUSINESS_STAFF = ["owner", "team_member", "team_lead"];
export const BUSINESS_EDITOR = ["owner", "team_member"];

export const roles = {
  Owner: "owner",
  Staff: ["team_member", "team_lead"],
  Client: "client",
};

/**
 * Comprehensive role information including display names, descriptions, and colors
 */
export const ROLE_INFO = {
  owner: {
    key: "owner",
    title: "Owner",
    description:
      "Full control over the account, can add team members and team leads, access all clients, and manage all business settings.",
    color: "#9333EA",
  },
  team_lead: {
    key: "team_lead",
    title: "Team Lead",
    description:
      "Can view all clients (even unassigned), assign clients to team members, and invite new team members. Managed by return allocation policy.",
    color: "#0284C7",
  },
  team_member: {
    key: "team_member",
    title: "Team Member",
    description:
      "Can only view clients assigned to them. Managed by return allocation policy.",
    color: "#16A34A",
  },
} as const;

/**
 * Get invitable role options (excludes owner)
 */
export const getInvitableRoles = () => {
  return [ROLE_INFO.team_lead.title, ROLE_INFO.team_member.title];
};

//answers for Jaz AI support default questions
export const answers = {
  taxorganizer: {
    0: `Hello there! 🌟

When you're gearing up to file your taxes, it's super helpful to have everything organized. Here’s a friendly checklist to make sure you're all set:

1. **Gather Your Tax Documents**: This includes forms like your W-2, 1099-MISC, 1099-NEC, 1099-G, and any other relevant documents. Having these handy will ensure you have all the necessary information.

2. **Spouse and Dependent Information**: If you're filing jointly or claiming dependents, remember to collect their personal details. This includes names, Social Security numbers, and dates of birth. Don’t forget to gather their tax documents too!

3. **Banking Details for Refunds**: If you’re looking forward to a direct deposit of your refund, make sure to have your bank account number and routing number ready. This will help in getting your refund processed smoothly and quickly.

Having all this information at your fingertips will make the tax filing process a breeze. If you need any more help or have questions, feel free to reach out! We’re here to assist you every step of the way. 😊`,
    1: `Hello there! 🌟

Let's get started with your tax journey! Simply click on the 'Get Started' button to begin. The Tax Organizer is here to guide you through each step, from entering your personal information to detailing your income and any deductions or credits you might have.

As you progress, don't worry about losing your work. Your information is automatically saved, allowing you to pause and return to it whenever it's convenient for you.

Once you've filled in all the necessary details, take a moment to review everything. It's important to ensure that all information is accurate and complete.

When you're ready, go ahead and submit your completed organizer. Your tax preparer will then review it. We've designed the Tax Organizer to be user-friendly and stress-free, but if you encounter any issues or have questions, please feel free to reach out for assistance. We're here to help!`,
  },
  clienthome: {
    0: `Hello there! I'm here to help you with your tax filing process. Here's a friendly guide on what you'll need to prepare:

1. **Earned Income**: Please upload your W-2 forms from all employers to report your earned income.

2. **Other Sources of Income**: If you have additional income sources, gather your 1099 forms. This includes 1099-MISC for miscellaneous income, 1099-NEC for non-employee compensation, and 1099-G for government payments.

3. **Deductions and Credits**: Don't forget to upload documents related to deductions and credits, such as mortgage interest statements (Form 1098), property tax records, and receipts for charitable donations.

4. **Education Expenses**: If you have education expenses, make sure to include Form 1098-T, and for any student loan interest paid, include Form 1098-E.

5. **Identity Protection**: Lastly, if you have an Identity Protection Personal Identification Number (IP PIN) from the IRS, keep that handy as well.

By having all these documents ready to upload, you'll ensure a smooth and efficient tax filing process. If you need any further assistance, feel free to ask!`,
    1: `Hello! Let's quickly upload your tax documents:

1. **Head to 'Documents':**
   Start by navigating to the 'Document' icon in the sidebar under the Home icon on your screen. Click on it and this will take you to the Documents page.

2. **Upload Your Files:**
   Click the 'ADD NEW' button, select your W-2, 1099, and other forms, then click 'Open' to upload them. Remember to label each file for easy reference.

3. **Review and Add More:**
   After uploading, check the list to ensure everything is correct. If you need to add more documents, just repeat the process.

4. **Proceed with Filing:**
   With all your documents uploaded, you're ready to proceed with your tax filing.

5. **Need Help?**
   If you encounter any issues or have questions, don't hesitate to reach out. We're here to assist you.

Good luck with your tax filing!`,
  },
  documents: {
    "0": `Hello! Let's make uploading your tax documents a breeze. Here’s how to do it:

1. **Add New Files**: First, click on the 'ADD NEW' button. This will open a file selection window where you can pick your W-2 forms, 1099 forms, and other necessary documents from your computer.

2. **Select and Label Your Files**: Choose the files you need and click 'Open' to start the upload. Remember to label each file correctly for easy identification later.

3. **Review and Upload More if Needed**: After uploading, check the list of documents to ensure everything is in order. If you need to add more, just repeat the process.

With all your documents uploaded, you’re set to proceed with your tax filing. If you have any questions or encounter any issues, feel free to reach out for assistance. We’re here to help you every step of the way!`,
    "1": `Hello there! Let's find that document you're looking for. 📄

On the Documents page, you'll find a neat list of all your uploaded documents waiting for you. To take a closer look at one, simply click on its name or the cute little icon right beside it. This will whisk you away to a new window where the document will open up, ready for your viewing pleasure.

And remember, if you ever feel a bit lost or need a helping hand, our friendly support team is just a message away. Don't hesitate to reach out – we're here to help! 😊`,
    "2": `Hi there! 😊 If your document isn't uploading, let's troubleshoot together.

First, ensure your file is in the correct format (.doc, .docx, .jpg, .jpeg, or .pdf) and under ${MAX_FILE_SIZE_MB}MB. Check if the document is corrupted, especially if it's been renamed to a different extension without proper conversion. If needed, rename the file to remove special characters.

Next, ensure a stable internet connection, clear your browser cache, or try a different browser/device. Keep your browser and OS updated.

If issues persist, our customer support is here to help! 🌟`,
  },
  vault: {
    "0": `Hello there! If you haven't received an IP PIN (Identity Protection Personal Identification Number) from the IRS, don't worry about it. You can simply leave the IP PIN field blank on your tax form. It's not mandatory unless you've been given one specifically.

Just to clarify, an IP PIN is a special code that helps protect your identity when you file your taxes. If you haven't been assigned one, you're good to go without it. You can continue with your tax filing as usual.

If you have any more questions or need a hand with anything else, just let us know. We're here to help you out every step of the way!`,
    "1": `Hello there! I'm here to assure you that your sensitive information is in safe hands with us. 
    
We understand the importance of keeping your personal details secure. That's why we've implemented several robust security measures to protect your data.

One of the key ways we ensure your information's safety is through advanced encryption. This technology is used both during the transmission of your data and when it's stored, ensuring that your personal details, such as your Social Security number and bank information, remain confidential and inaccessible to unauthorized parties.

Rest assured, your privacy and security are our top priorities. We're committed to keeping your information safe so you can enjoy peace of mind while using our services. If you have any questions or need further assistance, feel free to reach out. We're here to help!`,
  },
  settings: {
    "0": `Hello there! 😊 If you're looking to update your email address, I'm here to help guide you through the process. Since the email field is usually secured for your safety, you'll need to reach out to our friendly customer support team.

You can easily contact them at [help@smartwiz.io](mailto:help@smartwiz.io) 

Click the above button and drop them a message with your current email address, the new one you'd like to set up, and any details needed to verify your identity. This helps ensure your account stays secure and only you can make these changes.

The support team will be more than happy to assist you further and make the necessary updates to your account. If you have any questions or run into any issues along the way, don't hesitate to ask. They're there to help you every step of the way! 🌟`,
    "1": `Hello there! When it comes to setting up a new password, it's super important to make it both strong and secure. Here's a quick guide to help you create a password that keeps your account safe:

Firstly, your password should be at least 8 characters long. This gives it a good foundation for security. Also, mix it up with both uppercase and lowercase letters to add an extra layer of complexity. Don't forget to include at least one number; this makes it harder for anyone to guess.

Lastly, sprinkle in some special characters like !, @, #, or $. These little symbols can really boost your password's strength. And remember, avoid using common words or personal details like your name or birthdate. Following these tips will help ensure your account is well-protected from any unauthorized access.

Feel free to reach out if you need any more help!`,
    "2": `Hello there! If you're having trouble saving changes on the settings page, don't worry, we're here to help you sort it out. 

First things first, please double-check that all the necessary fields are filled in correctly. Sometimes, a simple oversight like leaving a field empty can prevent the save function from working. Also, look out for any error messages or highlighted fields that might be pointing to specific issues that need your attention. It could be something like not meeting password requirements or format restrictions for certain fields.

If everything looks good on your end, but you're still unable to save, it might be worth checking your internet connection to ensure it's stable. Sometimes, connectivity issues can cause hiccups. If you're still facing difficulties, you could try clearing your browser cache or switching to a different browser. If the issue persists, our friendly customer support team is just a message away and ready to assist you further.`,
  },
};

export const tones = [
  "Excited 🎉",
  "Appreciative 🙏",
  "Friendly 😊",
  "Encouraging 👍",
  "Grateful 🌟",
];

export const aiEmailTooltips = {
  reason: "Write a reason for the email",
  footer: "Write a footer for the email",
  subject: "Write a subject for the email",
  tone: "Select a tone for the email",
  sendto: "Recipient of the email",
};

export const progressSteps = [
  {
    label: "Tax Organzier Not Started",
    icon: IconBrowser,
    color: "#67B574",
  },

  {
    label: "Tax Organizer In Progress",
    icon: IconLoader,
    color: "#67B574",
  },
  {
    label: "Tax Organizer Submitted",
    icon: IconBrowserCheck,
    color: "#67B574",
  },
];

export const forms = [
  "W2",
  "1098E",
  "1098T",
  "1040",
  "1099NEC",
  "1099MISC",
  "1099G",
  "1099R",
  "1099K",
  "1099B",
  "1099C",
  "1099DA",
  "1099OID",
  "1099S",
  "1098",
  "Schedule A",
  "1095A",
  "1095B",
  "1095C",
  "1099INT",
  "1099DIV",
  "1099SA",
  "1099LTC",
  "1099SSA",
  "K1",
  "Driver's License",
  "Business Expense",
  "Business",
  "Childcare",
  "Other",
];

export const FORM2ID: Record<string, string> = {
  "Driver's License": "id",
  "Business Expense": "business_expense",
  W2: "w2",
  "1099NEC": "1099nec",
  "1099MISC": "1099misc",
  "1099G": "1099g",
  "1099R": "1099r",
  "1099K": "1099k",
  "1099INT": "1099int",
  "1099DIV": "1099div",
  "1099B": "1099b",
  "1099C": "1099c",
  "1099DA": "1099da",
  "1099OID": "1099oid",
  "1099S": "1099s",
  "1099SA": "1099sa",
  "1099LTC": "1099ltc",
  "1099SSA": "1099ssa",
  K1: "k1",
  "1095A": "1095a",
  "1095B": "1095b",
  "1095C": "1095c",
  "1098": "1098",
  "1098T": "1098t",
  "1098E": "1098e",
  "Schedule A": "scha",
  "1040": "1040",
  Childcare: "childcare",
  Other: "other",
  Business: "business",
};

export const LAST_ORGANIZER_YEAR = 2020;

// Routes dictionary for type-safe navigation
export const AppRoutes = {
  // Main routes
  root: "/",
  home: "/home",
  homeLite: "/home/lite",
  homeLiteSettings: "/home/lite/settings",
  onboarding: "/onboarding",
  gettingStarted: "/getting-started",
  liteOnboarding: "/lite/onboard",

  // Client routes
  clients: "/clients",
  clientProfile: "/clients/:userId/:taxYear?",
  clientHome: "/clienthome",

  // Tax organizer routes
  taxOrganizerHome: "/taxorganizerHome",
  taxOrganizer: "/taxorganizer",
  taxOrganizerYear: "/taxorganizer/:year",

  // User management routes
  settings: "/settings",
  returnSettings: "/settings?tab=Billing",
  referralSettings: "/settings?tab=Referral",
  teamSettings: "/settings?tab=Team",
  reports: "/reports",
  payments: "/payments",

  // Alpha groups
  alphaSignup: "/alpha/:slug",

  // Authentication routes
  login: "/login",
  logout: "/logout",
  register: "/register",
  registerPro: "/register/pro",
  registerPayment: "/register/payment",
  forgotPassword: "/forgotpassword",
  resetPassword: "/resetpassword",
  resetPasswordConfirm: "/resetpassword/confirm",
  verify: "/verify",

  // Other routes
  resources: "/resources",
  success: "/success",
  extensionUninstalled: "/extension-uninstalled",
  paymentUpdate: "/payment-update",
  /** Temporary contract upgrade campaign (owner CRM). */
  contractUpgrade: "/contract-upgrade",
  /** CRM route that redirects to intake `/pro/service-bureau/home` (bureau users). */
  serviceBureau: "/service-bureau",
  subscriptionExpired: "/subscription-expired",
  /** Contract/subscription mismatch — support-only, no self-serve reactivation. */
  billingAccountIssue: "/billing-account-issue",

  // Admin routes
  adminReturnDebug: "/admin/returns/:returnId",
  adminTimestepDetail: "/admin/returns/:returnId/timesteps/:timestepId",
  adminIntegrationLogs: "/admin/integrations/logs/:userId",
  adminAnalytics: "/admin/analytics",

  // Business slug routes
  businessSlug: "/:businessSlug/*",
  businessLogin: "/login",
  businessRegister: "/register",
  businessForgotPassword: "/forgotpassword",
  businessResetPasswordConfirm: "/resetpassword/confirm",
  // Managed-business onboarding invite (under :businessSlug). The link is
  // distinct from the password reset flow so legacy bookmarks cannot
  // silently route into the new "set password and auto-login" page.
  businessManagedBusinessInvite: "/managed-business/invite",
  businessCustomContractOfferInvite: "/custom-contract-offer/invite",

  // Catch all
  notFound: "*",
} as const;

/**
 * List of AppRoutes that we should skip authentication on.
 * ---
 */
export const SKIP_AUTH_PATHS = [
  AppRoutes.logout,
  AppRoutes.login,
  AppRoutes.resetPasswordConfirm,
  AppRoutes.forgotPassword,
  "/verify-email", // Don't know what this references but scared to delete - Alberto
  AppRoutes.verify,
  AppRoutes.register,
  AppRoutes.registerPro,
  AppRoutes.gettingStarted,
  AppRoutes.extensionUninstalled,
  // Managed-business invite link is anonymous: token is the credential.
  // Path lives under :businessSlug, so match the suffix.
  AppRoutes.businessManagedBusinessInvite,
  AppRoutes.businessCustomContractOfferInvite,
  AppRoutes.billingAccountIssue,
  "/alpha",
];

export const ifSkipAuth = (currentPath: string) => {
  // Remove query parameters and hash from the path
  const pathWithoutParams = currentPath.split("?")[0].split("#")[0];
  return SKIP_AUTH_PATHS.some(
    (path) =>
      pathWithoutParams.endsWith(path) || pathWithoutParams.startsWith(path)
  );
};

export const Products = {
  P1: "prod_RqvLAu47G6IpCP",
  P2: "prod_R35hP9thq7vQPx",
};

export const imgCDN = "https://d1ltrt1u8716a5.cloudfront.net";
export const extCDN = "https://d1ltrt1u8716a5.cloudfront.net/Chrome-Extension";

export const AppResources = {
  // Accounts
  createAccount: `${imgCDN}/Smartwiz-Product/Misc/Create-account.svg`,
  // Credits
  wizzyCoin: `${imgCDN}/Smartwiz-Product/Credits-System/WizCoin.svg`,
  // Branding
  wizzyWithGlases: `${imgCDN}/Logos and Branding/smartwiz logos/sw-head-glasses.svg`,
  logo: `${imgCDN}/Logos+and+Branding/smartwiz+logos/smartwiz+black.png`,
  facebookLogo: `${imgCDN}/Logos and Branding/public/FacebookLogo.png`,
  googleLogo: `${imgCDN}/Logos and Branding/public/GoogleLogo.webp`,
  // Demos
  demoHeader: `${imgCDN}/Smartwiz-Product/Demo-Resource/demo-header.png`,
  demoHeaderVideo: `${imgCDN}/Smartwiz-Product/Demo-Resource/video-header.png`,
  demoBook: `${imgCDN}/Smartwiz-Product/Demo-Resource/book-one-call.png`,
  demoTaxTuesday: `${imgCDN}/Smartwiz-Product/Demo-Resource/tax-tuesday.png`,
  demoWildWednesday: `${imgCDN}/Smartwiz-Product/Demo-Resource/wild-wed.png`,
  // Tax Pro Onboarding
  onboardingVerification: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/verification-onboarding.png`,
  taxwiseLiteHome: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/TaxWiseLiteHome.png`,
  taxslayerLiteHome: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/TaxSlayerLiteHome.png`,
  extensionLiteHome: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/chomeExtensionLiteHome.png`,
  wizzyLiteHome: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/LiteHomeWizzy.png`,
  extensionOnboarding: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/extension-onboarding.png`,
  downloadExtension: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/Download Extension.gif`,
  onboardingLiteSupport: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/liteOnboardSupport.png`,
  onboardingSampleClient: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/sw-sample-client-row.png`,
  onboardingPinningExtension: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/Pinning Extension.gif`,
  onboardingOpeningExtension: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/OpeningYourExtension.gif`,
  onboardingLiteLoginStep: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/LiteLoginStep.gif`,
  onboardingLiteExtensionStep: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/LiteExtensionStep.gif`,
  onboardingLiteOneClickStep: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/LiteOneClickStep.gif`,
  onboardingLiteNewReturnStep: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/LiteNewReturnStep.gif`,
  onboardingLiteViewClientStep: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/liteOnboardView.png`,
  onboardingLiteUnlockReturnStep: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/liteOnboardUnlock.png`,
  onboardingLiteOnboardStep: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/liteOnboardButton.png`,
  onboardingLiteTaxWiseVideo: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/LiteTaxWiseVideo.mp4`,
  onboardingLiteTaxSlayerVideo: `${imgCDN}/Smartwiz-Product/Taxpro-Onboarding/LiteTaxSlayerVideo.mp4`,
  gettingStartedThumbnail: `${imgCDN}/walkthroughs/getting-started/GettingStartedThumbnail.png`,
  gettingStartedVideoUrl:
    "https://www.loom.com/share/e393adb5a12e4a7eaddbe4d2ad38915e",
  gettingStartedVideoId: "e393adb5a12e4a7eaddbe4d2ad38915e",
  // Support
  supportHubUrl: "https://hello.smartwiz.ai/wizzy-support-hub",
  // Misc
  glassesLogo: `${imgCDN}/Smartwiz-Product/Misc/Glasses-Logo.png`,
  subscriptionExpired: `${imgCDN}/Smartwiz-Product/Misc/subscription-expired.png`,
  invalidPayment: `https://app.smartwiz.io/public/images/WizzyOops.png`,
  requestConfirmation: `${imgCDN}/Smartwiz-Product/Misc/request-confirmation.png`,
  noPayments: `${imgCDN}/Smartwiz-Product/Misc/No-payments.png`,
  wallet: `${imgCDN}/Smartwiz-Product/Misc/Wallet-rafiki+1.png`,
  celebration: `${imgCDN}/Smartwiz-Product/Misc/Celebration-pana+1.svg`,
  userAgreement: `${imgCDN}/Smartwiz-Product/Misc/2025SmartWizUserAgreement.pdf`,
  clientCreationWelcome: `${imgCDN}/Smartwiz-Product/Misc/client-creation-welcome.png`,
  clientCreationModal: `${imgCDN}/Smartwiz-Product/Misc/create-client-modal.svg`,
  // Wizzy
  wizzyDownload: `${imgCDN}/Wizzy/WizzyDownload.png`,
  wizzyId: `${imgCDN}/Wizzy/WizzyId.png`,
  wizzyFind: `${imgCDN}/Wizzy/WizzyFind.png`,
  wizzyPayment: `${imgCDN}/Wizzy/WizzyPayment.png`,
  wizzyPuzzle: `${imgCDN}/Wizzy/WizzyPuzzle.png`,
  wizzyToggle: `${imgCDN}/Wizzy/WizzyToggle.png`,
  wizzyWand: `${imgCDN}/Wizzy/WizzyWand.png`,
  wizzyOnboxing: `${imgCDN}/Wizzy/WizzyOnboxing.png`,
  wizzyDownload2: `${imgCDN}/Wizzy/wizzyDownload2.png`,
  wizzyOnboard: `${imgCDN}/Wizzy/WizzyOnboard.png`,
  wizzyWave: `${imgCDN}/Wizzy/wizzyWave.png`,
  wizzyFeedback: `${imgCDN}/Wizzy/Feedback-removebg-preview.png`,
  wizzyMagician: `${imgCDN}/Wizzy/WizzyMagician.png`,
  wizzyMegaphone: `${imgCDN}/Wizzy/WizzyMegaphone.png `,
  wizzyRocket: `${imgCDN}/Wizzy/WizzyRocket.png `,
  wizzy2: `${imgCDN}/marketing+images/wizzy2.0.png`, // Wizzy 2.0 branding image
  wizzyHero: `${imgCDN}/marketing+images/wizzyhero.png`, // Wizzy hero image (on rocket)
  wizzyBalloon: `${imgCDN}/marketing+images/wizzy-balloon.png`, // Wizzy with balloon
  wizzyExtension: `${imgCDN}/marketing+images/WizzyExtension.png`, // Extension feature with Wizzy
  wizzyHIW1: `${imgCDN}/marketing+images/WizzyHIW1.png`, // How it works step 1 - Choose payment
  wizzyHIW2: `${imgCDN}/marketing+images/WizzyHIW2.png`, // How it works step 2 - Add Wizzy fee
  wizzyHIW4: `${imgCDN}/marketing+images/WizzyHIW4.png`, // How it works step 4 - Fee collection
  // Marketing - General
  speedTransparent: `${imgCDN}/marketing images/speed_transparent_background.png`, // Speed/performance illustration
  badge: `${imgCDN}/marketing+images/badge.png`, // Badge graphic
  smartwizHead: `${imgCDN}/marketing+images/smartwiz+head.png`, // SmartWiz head logo
  // Marketing - Features
  clientCreation: `${imgCDN}/marketing+images/clientcreation.png`, // Client creation feature showcase image
  oneClickReturn: `${imgCDN}/marketing+images/oneclickreturn.png`, // One-click return feature showcase image
  reviewImage: `${imgCDN}/marketing+images/review.png`, // Review feature showcase image
  integrations: `${imgCDN}/marketing+images/Integrations.png`, // Integrations feature
  organize: `${imgCDN}/marketing+images/Organize.png`, // Organize feature
  more: `${imgCDN}/marketing+images/More.png`, // More features
  tryMe: `${imgCDN}/marketing+images/TryMe.png`, // Try me CTA graphic
  // Marketing - Payment
  payByCard: `${imgCDN}/marketing+images/Pay-by-card.png`, // Pay by card option
  payByRefund: `${imgCDN}/marketing+images/Pay-by-refund.png`, // Pay by refund option
  // Marketing - Partner Logos
  oneStopTaxesLogo: `${imgCDN}/marketing+images/one+stop+taxes+logo.png`, // One Stop Taxes partner logo
  taxesAndPRLogo: `${imgCDN}/marketing+images/taxesandpr.png`, // Taxes and PR partner logo
  swLogo: `${imgCDN}/marketing+images/sw.png`, // SW logo
  // Marketing Videos
  homeHeroVideo: `${imgCDN}/videos/HomeHeroVideo.mp4`, // Home page hero video
  taxOrganizerVideo: `${imgCDN}/videos/tax-organizer.mp4`, // Tax organizer workflow demonstration
  wizzyExtractionVideo: `${imgCDN}/videos/wizzyextraction.mp4`, // Wizzy document extraction demonstration
  oneClickMagicVideo: `${imgCDN}/videos/one-click-magic.mp4`, // One-click magic feature demonstration
  reviewDemoVideo: `${imgCDN}/videos/ReviewDemo.mp4`, // Review workflow demonstration
  // Marketing - Testimonials
  cambriaThumbnail: `${imgCDN}/marketing+images/testimonials/testimonial-videos/cambria-thumbnail.png`, // Cambria testimonial thumbnail
  ddThumbnail: `${imgCDN}/marketing+images/testimonials/testimonial-videos/dd-thumbnail.png`, // DD testimonial thumbnail
  ellisThumbnail: `${imgCDN}/marketing+images/testimonials/testimonial-videos/ellis-thumbnail.png`, // Ellis testimonial thumbnail
  // Extension - Loaders & Animations
  swPageLoad: `${extCDN}/wizziePageLoad.gif`, // Wizzy page loading animation
  swFillForm: `${extCDN}/wizzieFillForm.gif`, // Wizzy filling form animation
  swNavigate: `${extCDN}/wizzieFindForm.gif`, // Wizzy finding form animation
  swGlasses: `${extCDN}/sw-glasses.gif`, // SmartWiz glasses animation
  smartsync: `${extCDN}/smartsync.gif`, // SmartSync animation
  wizzyText: `${extCDN}/wizzyText.gif`, // Wizzy text animation
  wizzyFixError: `${extCDN}/wizzyFixError.gif`, // Wizzy fixing error animation
  // Extension - UI Icons
  extImport: `${extCDN}/client-creation/main_import.svg`, // Import icon for extension
  extCreate: `${extCDN}/client-creation/main_create.svg`, // Create icon for extension
  extList: `${extCDN}/client-creation/main_list.svg`, // List icon for extension
  loginWizzy: `${extCDN}/client-creation/login_wizzy.svg`, // Login Wizzy icon
  profileCoin: `${extCDN}/client-profile/profile_coin.svg`, // Profile coin icon
  // Integration Logos
  jotformLogo: `${imgCDN}/icons/jotform-logo.webp`, // JotForm integration logo
  suitedashLogo: `${imgCDN}/icons/suitedash-logo.png`, // SuiteDash integration logo
  cognitoLogo: `${imgCDN}/icons/cognito-logo.png`, // Cognito Forms integration logo
  ghlLogo: `${imgCDN}/icons/ghl-logo.svg`, // GoHighLevel integration logo
};

export const EXTENSION_DOWNLOAD_URL =
  "https://chromewebstore.google.com/detail/smartsync-one-click-retur/lopfgdkmhlpffjbcehlcipigognpaada?hl=en&authuser=0";
